import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Tooltip, IconButton, Menu, MenuItem } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import TimerIcon from "@material-ui/icons/Timer";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    height: 32,
    '&[aria-disabled="true"]': {
      opacity: 0.5,
      borderColor: "rgba(0, 0, 0, 0.12)",
    },
  },
  refreshButton: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    padding: 4,
  },
  dropdownButton: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: `1px solid ${theme.palette.divider}`,
    padding: 4,
  },
  countdown: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: "10px",
    fontWeight: "bold",
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    width: 16,
    height: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
  },
}));

/**
 * RefreshAction encapsulates manual and auto-refresh controls for an execution.
 *
 * Props:
 * - execution: workflow execution object (used to determine terminal states)
 * - onRefresh: function to invoke when a refresh is required (should close panel and refetch)
 */
const RefreshAction = ({ execution, onRefresh }) => {
  const classes = useStyles();
  const [refreshInterval, setRefreshInterval] = useState(0); // seconds; 0 => manual
  const [anchorEl, setAnchorEl] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(null);
  const prevExecutionStatusRef = useRef(null);
  const refreshIntervalIdRef = useRef(null);

  const isTerminal = useMemo(() => {
    if (!execution) return false;
    return (
      execution.status === "COMPLETED" ||
      execution.status === "FAILED" ||
      execution.status === "TERMINATED" ||
      execution.status === "TIMED_OUT" ||
      execution.status === "PAUSED"
    );
  }, [execution]);

  const handleMenuOpen = useCallback(
    (event) => setAnchorEl(event.currentTarget),
    []
  );
  const handleMenuClose = useCallback(() => setAnchorEl(null), []);

  const stopAutoRefresh = useCallback(() => {
    if (refreshIntervalIdRef.current) {
      clearInterval(refreshIntervalIdRef.current);
      refreshIntervalIdRef.current = null;
    }
    setRefreshInterval(0);
    setCountdownSeconds(null);
  }, []);

  const startAutoRefresh = useCallback(
    (intervalSeconds) => {
      if (refreshIntervalIdRef.current) {
        clearInterval(refreshIntervalIdRef.current);
      }
      if (!intervalSeconds || intervalSeconds <= 0) {
        stopAutoRefresh();
        return;
      }
      setRefreshInterval(intervalSeconds);
      setCountdownSeconds(intervalSeconds);
      const id = setInterval(() => {
        setCountdownSeconds((prev) => {
          const next = (prev ?? intervalSeconds) - 1;
          if (next <= 0) {
            onRefresh?.();
            return intervalSeconds;
          }
          return next;
        });
      }, 1000);
      refreshIntervalIdRef.current = id;
    },
    [onRefresh, stopAutoRefresh]
  );

  const handleRefreshIntervalChange = useCallback(
    (intervalSeconds) => {
      handleMenuClose();
      if (intervalSeconds > 0) {
        startAutoRefresh(intervalSeconds);
      } else {
        stopAutoRefresh();
      }
    },
    [startAutoRefresh, stopAutoRefresh, handleMenuClose]
  );

  useEffect(() => {
    const prevStatus = prevExecutionStatusRef.current;
    const wasTerminal =
      prevStatus === "COMPLETED" ||
      prevStatus === "FAILED" ||
      prevStatus === "TERMINATED" ||
      prevStatus === "TIMED_OUT" ||
      prevStatus === "PAUSED";
    if (isTerminal && !wasTerminal) {
      stopAutoRefresh();
      onRefresh?.();
    }
    prevExecutionStatusRef.current = execution?.status;
  }, [execution, isTerminal, onRefresh, stopAutoRefresh]);

  useEffect(() => {
    const intervalId = refreshIntervalIdRef.current;
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  const handleManualRefreshClick = useCallback(() => {
    if (isTerminal) return;
    onRefresh?.();
    if (refreshInterval > 0) {
      setCountdownSeconds(refreshInterval);
    }
  }, [isTerminal, onRefresh, refreshInterval]);

  const tooltipTitle = useMemo(() => {
    if (isTerminal && execution)
      return `Workflow ${execution.status.toLowerCase()}`;
    return refreshInterval === 0
      ? "Refresh"
      : `Auto-refresh in ${countdownSeconds}s`;
  }, [isTerminal, execution, refreshInterval, countdownSeconds]);

  const menuItems = [
    { label: "Manual Refresh", value: 0 },
    { label: "Auto-refresh every 3s", value: 3 },
    { label: "Auto-refresh every 5s", value: 5 },
    { label: "Auto-refresh every 7s", value: 7 },
  ];

  return (
    <div className={classes.root} aria-disabled={isTerminal}>
      <Tooltip title={tooltipTitle}>
        {/* The span is necessary for the tooltip to work on a disabled button */}
        <span>
          <IconButton
            onClick={handleManualRefreshClick}
            disabled={isTerminal}
            className={classes.refreshButton}
            color="primary"
          >
            {refreshInterval === 0 ? (
              <RefreshIcon />
            ) : (
              <>
                <TimerIcon />
                <div className={classes.countdown}>{countdownSeconds}</div>
              </>
            )}
          </IconButton>
        </span>
      </Tooltip>
      <IconButton
        onClick={handleMenuOpen}
        disabled={isTerminal}
        className={classes.dropdownButton}
        aria-controls="refresh-menu"
        aria-haspopup="true"
      >
        <ArrowDropDownIcon />
      </IconButton>
      <Menu
        id="refresh-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {menuItems.map(({ label, value }) => (
          <MenuItem
            key={value}
            selected={refreshInterval === value}
            onClick={() => handleRefreshIntervalChange(value)}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default React.memo(RefreshAction);
